import { useState, type ReactNode } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { useScene } from '../scene-context';

export interface LiftVeilProps {
  children: ReactNode;
  primaryColor?: string;
  secondaryColor?: string;
  backgroundColor?: string;
  textColor?: string;
  /** Overrides the outer wrapper's layout classes (default keeps the original narrow reading-width column). */
  className?: string;
  /** Label shown on the curtain before it's lifted. Defaults to the generic "Antarpat" (Hindu ritual veil). */
  label?: string;
}

export function LiftVeil({
  children,
  primaryColor,
  secondaryColor,
  backgroundColor,
  textColor,
  className,
  label = 'Antarpat',
}: LiftVeilProps) {
  let colors = {
    primary: primaryColor || '#8B1A1A',
    secondary: secondaryColor || '#C8A032',
    background: backgroundColor || '#1C0608',
    text: textColor || '#F7EED6',
  };

  try {
    const scene = useScene();
    if (scene?.pack?.colors) {
      colors = {
        primary: primaryColor || scene.pack.colors.primary,
        secondary: secondaryColor || scene.pack.colors.secondary,
        background: backgroundColor || scene.pack.colors.background,
        text: textColor || scene.pack.colors.text,
      };
    }
  } catch {
    // Standalone fallback when rendered outside SceneProvider
  }

  const reduceMotion = useReducedMotion();
  const [lifted, setLifted] = useState(Boolean(reduceMotion));

  return (
    <div className={className ?? 'relative z-10 w-full max-w-3xl px-6 text-center'}>
      <motion.div
        role="button"
        tabIndex={lifted ? -1 : 0}
        aria-label="Lift the antarpat"
        drag="y"
        dragConstraints={{ top: -180, bottom: 0 }}
        onDragEnd={(_, info) => {
          if (info.offset.y < -60) setLifted(true);
        }}
        onClick={() => setLifted(true)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setLifted(true);
          }
        }}
        className={`absolute inset-x-4 -top-20 bottom-0 z-20 ${lifted ? 'pointer-events-none' : 'cursor-grab pointer-events-auto'}`}
        style={{
          borderColor: `${colors.primary}44`,
          borderWidth: 1,
          borderStyle: 'solid',
          borderBottom: 'none',
          borderRadius: '0 0 40% 40% / 0 0 6% 6%',
          // Layered: base drape gradient + vertical pleat folds + subtle sheen highlight
          background: `
            linear-gradient(90deg,
              rgba(0,0,0,0.16) 0%, transparent 4%, transparent 8%, rgba(0,0,0,0.12) 12%,
              transparent 16%, transparent 22%, rgba(255,255,255,0.10) 26%, transparent 30%,
              transparent 38%, rgba(0,0,0,0.14) 42%, transparent 46%, transparent 54%,
              rgba(255,255,255,0.08) 58%, transparent 62%, transparent 70%, rgba(0,0,0,0.12) 74%,
              transparent 78%, transparent 84%, rgba(0,0,0,0.16) 88%, transparent 92%, transparent 100%
            ),
            radial-gradient(120% 60% at 50% 0%, ${colors.secondary}55, transparent 60%),
            linear-gradient(180deg, ${colors.secondary}f2, ${colors.primary}dd 42%, ${colors.background}55 100%)
          `,
          boxShadow: `0 30px 80px ${colors.background}cc, inset 0 -24px 36px -20px rgba(0,0,0,0.45)`,
        }}
        animate={{ y: lifted ? '-115%' : 0, opacity: lifted ? 0 : 1 }}
        transition={{ duration: reduceMotion ? 0.2 : 1.05, ease: [0.2, 0.8, 0.2, 1] }}
      >
        {/* Gold embroidered trim band */}
        <div
          className="absolute inset-x-0 bottom-6 h-[3px] opacity-80"
          style={{ background: `linear-gradient(90deg, transparent, ${colors.secondary}, transparent)` }}
          aria-hidden="true"
        />

        {/* Hanging tassel fringe along the bottom edge */}
        <div
          className="absolute inset-x-0 bottom-0 h-6"
          style={{
            backgroundImage: `repeating-linear-gradient(90deg, ${colors.secondary}99 0 2px, transparent 2px 10px)`,
            maskImage: 'linear-gradient(to bottom, black 0%, black 40%, transparent 100%)',
            WebkitMaskImage: 'linear-gradient(to bottom, black 0%, black 40%, transparent 100%)',
          }}
          aria-hidden="true"
        />

        {/* Center pendant medallion, like a ceremonial curtain's centerpiece */}
        <div
          className="absolute left-1/2 bottom-3 w-5 h-5 -translate-x-1/2 rotate-45 rounded-[3px]"
          style={{
            background: `linear-gradient(135deg, ${colors.secondary}, ${colors.primary})`,
            boxShadow: `0 4px 10px rgba(0,0,0,0.4)`,
          }}
          aria-hidden="true"
        />

        {!lifted && (
          <p
            className="absolute bottom-14 left-0 right-0 text-xs font-semibold uppercase"
            style={{ color: colors.text, letterSpacing: '0.15em' }}
          >
            {label}
          </p>
        )}
      </motion.div>
      <div>{children}</div>
    </div>
  );
}
